import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiChevronLeft, FiChevronRight, FiSearch, FiAlertOctagon, FiClock, FiAlertCircle,
  FiCheckCircle, FiClipboard, FiLock, FiEdit2, FiCheck, FiChevronUp, FiChevronDown, FiEye, FiEyeOff, FiX, FiFilter, FiRotateCcw, FiChevronsLeft, FiChevronsRight, FiPackage,
} from 'react-icons/fi';
import { buscarVencimentosRotinas, marcarRotina, buscarHistoricoRotina, salvarParticularidade, marcarSemMovimento, desmarcarSemMovimento, salvarObservacao, textoPeriodo, SETORES_ROTINA } from '../../services/rotinasProgramadas';
import useEsc from '../../hooks/useEsc';
import { atualizarTarefa, listarTarefas } from '../../services/tarefas';
import { listarBlocosRotina, listarPacotesRotina } from '../../services/rotinasProgramadas';
import { listarClientes } from '../../services/clientes';
import { DrawerTarefa, DrawerEditarGrupo, ModalTarefaDetalhe } from './Tarefas';
import { DrawerBloco, DrawerPacote } from './CadastroRotinas';
import css from './VisaoVencimentos.module.css';

/* ── Aba "Vencimentos" do Painel ──
   Lê as rotinas cadastradas no menu Obrigações > Rotinas (não mais os
   grupos da aba Etapas). Cabeçalho em duas linhas:
     1ª  o período do bloco (uma data só quando início = fim)
     2ª  nome da rotina, local e a data individual dela
   Tarefas da aba Tarefas entram como colunas no dia do vencimento (pedido
   do usuário em 31/10/2026; tinham saído em 13/10).
   Linhas = clientes marcados nas rotinas pelos processos. Clicar na bolinha
   marca/desmarca a rotina como concluída. Só entra aqui o que vem do menu
   Rotinas -- nada de tarefas, Nibo ou grupos da aba Etapas. */

function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function diasEntre(a, b) { // b - a, em dias, só data
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000);
}
const dataCurta = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
function competenciaAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function deslocar(comp, n) {
  let [y, m] = comp.split('-').map(Number);
  m += n;
  while (m > 12) { m -= 12; y += 1; }
  while (m < 1) { m += 12; y -= 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
}
/* largura do texto em px na fonte da tela -- usado pra o cabeçalho do bloco
   caber em DUAS linhas: se não couber, as colunas do bloco crescem por igual */
let canvasMedida = null;
function larguraTexto(texto, fonte) {
  if (!texto) return 0;
  try {
    canvasMedida = canvasMedida || document.createElement('canvas');
    const ctx = canvasMedida.getContext('2d');
    if (!ctx) throw new Error('sem canvas');
    const familia = getComputedStyle(document.body).fontFamily || 'sans-serif';
    ctx.font = `${fonte} ${familia}`;
    return ctx.measureText(texto).width;
  } catch {
    return texto.length * 7; // ambiente sem canvas: estimativa
  }
}
/* largura mínima de cada coluna do bloco pra o cabeçalho (local / título +
   competência) caber em duas linhas, sem quebrar mais */
function minimoPorColuna(b) {
  const linha1 = b.titulo ? larguraTexto((b.local || '').toUpperCase(), '800 10px') : 0;
  const principal = b.titulo || b.local || '';
  const linha2 = larguraTexto(b.titulo ? principal : principal.toUpperCase(), b.titulo ? '700 11.5px' : '800 10px')
    + (b.competencia ? larguraTexto('00/00', '800 10px') + 17 : 0);
  return Math.ceil((Math.max(linha1, linha2) + 14) / b.n);
}

function normalizar(t) {
  return (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/* regime = enquadramento do cadastro do cliente (MEI, Simples, Presumido,
   Real, Sem fins); sem enquadramento escolhido ainda, cai no que a Receita
   diz (MEI/Simples) e, sem nada, "—" */
const REGIMES = { mei: 'MEI', simples: 'Simples', presumido: 'Presumido', real: 'Real', sem_fins: 'Sem fins' };
const ORDEM_REGIME = ['MEI', 'Simples', 'Presumido', 'Real', 'Sem fins'];
function regimeDo(c) {
  if (c?.enquadramento && REGIMES[c.enquadramento]) return REGIMES[c.enquadramento];
  if (c?.opcao_mei || c?.opcaoMei) return 'MEI';
  if (c?.opcao_simples || c?.opcaoSimples) return 'Simples';
  return '—';
}
const CLASSE_REGIME = { MEI: 'regMei', Simples: 'regSimples', Presumido: 'regPresumido', Real: 'regReal', 'Sem fins': 'regSemFins' };

/* pendente ganha a cor pelo prazo da data individual */
function estadoPorPrazo(hoje, data) {
  const dias = diasEntre(hoje, data);
  if (dias < 0) return { tipo: 'vencido', dias };
  if (dias === 0) return { tipo: 'hoje', dias };
  if (dias <= 3) return { tipo: 'proximo', dias };
  return { tipo: 'prazo', dias };
}

const TEXTO_ESTADO = {
  concluido: 'Concluído', vencido: 'Vencido', hoje: 'Vence hoje',
  proximo: 'Atenção (próximos 3 dias)', prazo: 'Pendente, no prazo',
};

function Bolinha({ estado, titulo, onClick }) {
  if (!estado) return <span className={css.fora} title="Fora da rotina deste cliente">·</span>;
  return (
    /* pendente é sempre um círculo neutro -- o prazo aparece na cor da data
       do cabeçalho (verde/laranja/vermelho), pra grade ficar discreta */
    <span className={`${css.bola} ${css[estado.tipo === 'concluido' ? 'b_concluido' : 'b_prazo']} ${onClick ? css.clicavel : ''}`} title={titulo}
      onClick={onClick} role={onClick ? 'button' : undefined} />
  );
}

/* reabrir uma bolinha concluída exige o motivo -- vai pro histórico */
function ModalReabrir({ alvo, onConfirmar, onFechar }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  useEsc(onFechar);
  async function confirmar() {
    if (!motivo.trim()) return setErro('Informe o motivo da reabertura.');
    setEnviando(true); setErro('');
    try { await onConfirmar(motivo.trim()); }
    catch (e) { setErro(e.message); setEnviando(false); }
  }
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3><FiRotateCcw size={14} /> Reabrir rotina</h3>
            <p>{alvo.cliente} · {alvo.rotina}</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          <label className={css.rotulo}>Motivo da reabertura *</label>
          <textarea rows={3} value={motivo} autoFocus onChange={e => setMotivo(e.target.value)}
            placeholder="Ex.: guia emitida com valor errado, precisa refazer" />
          {erro && <p className={css.erro}>{erro}</p>}
        </div>
        <div className={css.modalRodape}>
          <button type="button" className={css.btnSec} onClick={onFechar}>Cancelar</button>
          <button type="button" className={css.btnPri} onClick={confirmar} disabled={enviando}>{enviando ? 'Reabrindo...' : 'Reabrir'}</button>
        </div>
      </div>
    </div>
  );
}

/* particularidade da etapa pra esse cliente: abre pra leitura; clicar no
   texto já deixa editar. Sem texto, já abre editando. Salvar vale do mês
   da ocorrência em diante (os anteriores ficam como estavam); vazio =
   sem anotação dali em diante. */
const mesAno = (comp) => `${comp.slice(5)}/${comp.slice(0, 4)}`;
function ModalParticularidade({ alvo, textoAtual, desde, onSalvar, onFechar }) {
  const [editando, setEditando] = useState(!textoAtual);
  const [texto, setTexto] = useState(textoAtual || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  useEsc(onFechar);
  async function salvar() {
    setSalvando(true); setErro('');
    try { await onSalvar(texto); }
    catch (e) { setErro(e.message); setSalvando(false); }
  }
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3>Particularidades</h3>
            <p>{alvo.cliente} · {alvo.rotina}</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          {editando ? (
            <textarea rows={6} value={texto} autoFocus onChange={e => setTexto(e.target.value)}
              placeholder="Ex.: o cliente manda as notas por e-mail até o dia 3; conferir retenção de ISS..." />
          ) : (
            <div className={css.textoLeitura} onClick={() => setEditando(true)} title="Clique para editar">{texto}</div>
          )}
          <p className={css.vigenciaNota}>
            {editando
              ? `Ao salvar, vale de ${mesAno(alvo.mes)} em diante. Os meses anteriores continuam como estavam.`
              : desde && desde !== '0000-00' ? `Anotação em vigor desde ${mesAno(desde)}.` : 'Anotação que vale todo mês.'}
          </p>
          {erro && <p className={css.erro}>{erro}</p>}
        </div>
        <div className={css.modalRodape}>
          <button type="button" className={css.btnSec} onClick={onFechar}>Fechar</button>
          {editando && (
            <button type="button" className={css.btnPri} onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* histórico da bolinha -- só gerente e administrador chegam aqui */
/* "sem movimento" (orelha de baixo): marca pergunta se precisou de declaração;
   marcado, mostra o que foi registrado e deixa desmarcar */
function ModalSemMovimento({ alvo, marcado, concluido, onMarcar, onDesmarcar, onFechar }) {
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  useEsc(onFechar);
  async function agir(fn) {
    setSalvando(true); setErro('');
    try { await fn(); } catch (e) { setErro(e.message); setSalvando(false); }
  }
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3>Sem movimento</h3>
            <p>{alvo.cliente} · {alvo.rotina} · {mesAno(alvo.prazo.slice(0, 7))}</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          {!marcado ? (
            <p className={css.perguntaSemMov}>Esta obrigação precisou de envio de <strong>declaração sem movimento</strong>?</p>
          ) : (
            <div className={css.semMovInfo}>
              <p>Marcado como <strong>sem movimento</strong>{marcado.usuario_nome || marcado.usuario_email ? ` por ${marcado.usuario_nome || marcado.usuario_email}` : ''}
                {marcado.em ? ` em ${new Date(marcado.em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}.</p>
              <p>{marcado.precisa_declaracao
                ? (concluido ? 'Declaração sem movimento enviada (concluída).' : 'Precisa enviar a declaração sem movimento: marque a bolinha quando enviar.')
                : 'Sem declaração: baixada com a justificativa de sem movimento.'}</p>
            </div>
          )}
          {erro && <p className={css.erro}>{erro}</p>}
        </div>
        <div className={css.modalRodape}>
          {!marcado ? (
            <>
              <button type="button" className={css.btnSec} onClick={onFechar} disabled={salvando}>Cancelar</button>
              <button type="button" className={css.btnSec} onClick={() => agir(() => onMarcar(false))} disabled={salvando}
                title="Baixa a obrigação com a justificativa de sem movimento">Não</button>
              <button type="button" className={css.btnPri} onClick={() => agir(() => onMarcar(true))} disabled={salvando}
                title="Registra e a bolinha fica pendente até marcar o envio">Sim</button>
            </>
          ) : (
            <>
              <button type="button" className={css.btnSec} onClick={() => agir(onDesmarcar)} disabled={salvando}>Desmarcar sem movimento</button>
              <button type="button" className={css.btnPri} onClick={onFechar}>Fechar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* observação da ocorrência (orelha de cima à direita): algo a resolver antes de
   concluir, só deste mês. Lê e edita como as particularidades. */
function ModalObservacao({ alvo, obs, onSalvar, onFechar }) {
  const [editando, setEditando] = useState(!obs);
  const [texto, setTexto] = useState(obs?.texto || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  useEsc(onFechar);
  async function salvar(t) {
    setSalvando(true); setErro('');
    try { await onSalvar(t); } catch (e) { setErro(e.message); setSalvando(false); }
  }
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3>Observação</h3>
            <p>{alvo.cliente} · {alvo.rotina} · {mesAno(alvo.prazo.slice(0, 7))}</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          {editando ? (
            <textarea rows={5} value={texto} autoFocus onChange={e => setTexto(e.target.value)}
              placeholder="Ex.: erro na Domínio ao gerar a guia; resolver antes de concluir." />
          ) : (
            <div className={css.textoLeitura} onClick={() => setEditando(true)} title="Clique para editar">{texto}</div>
          )}
          <p className={css.vigenciaNota}>
            {obs?.resolvida
              ? `Verificada e resolvida${obs.resolvida_por ? ` por ${obs.resolvida_por}` : ''}${obs.resolvida_em ? ` em ${new Date(obs.resolvida_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}.`
              : 'Vale só para este mês. Ao concluir, o sistema mostra esta observação e pergunta se ela foi resolvida.'}
          </p>
          {erro && <p className={css.erro}>{erro}</p>}
        </div>
        <div className={css.modalRodape}>
          {obs && <button type="button" className={css.btnSec} onClick={() => salvar('')} disabled={salvando} style={{ marginRight: 'auto' }}>Apagar</button>}
          <button type="button" className={css.btnSec} onClick={onFechar}>Fechar</button>
          {editando && <button type="button" className={css.btnPri} onClick={() => salvar(texto)} disabled={salvando || !texto.trim()}>{salvando ? 'Salvando...' : 'Salvar'}</button>}
        </div>
      </div>
    </div>
  );
}

/* concluir com observação pendente: mostra e pergunta se foi resolvida */
function ModalConfirmarObservacao({ alvo, onSim, onFechar }) {
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  useEsc(onFechar);
  async function sim() {
    setSalvando(true); setErro('');
    try { await onSim(); } catch (e) { setErro(e.message); setSalvando(false); }
  }
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3>Observação pendente</h3>
            <p>{alvo.cliente} · {alvo.rotina}</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          <div className={css.obsDestaque}>{alvo.texto}</div>
          <p className={css.perguntaSemMov}>Você verificou e <strong>resolveu</strong> essa questão pendente?</p>
          {erro && <p className={css.erro}>{erro}</p>}
        </div>
        <div className={css.modalRodape}>
          <button type="button" className={css.btnSec} onClick={onFechar} disabled={salvando} title="Não conclui: resolva a questão antes">Não, voltar</button>
          <button type="button" className={css.btnPri} onClick={sim} disabled={salvando}>{salvando ? 'Concluindo...' : 'Sim, concluir'}</button>
        </div>
      </div>
    </div>
  );
}

/* a rotina está em mais de um processo: escolher qual abrir */
function ModalEscolherProcesso({ dados, onEscolher, onFechar }) {
  useEsc(onFechar);
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3>Qual processo?</h3>
            <p>A rotina "{dados.rotina}" está em {dados.opcoes.length} processos.</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          {dados.opcoes.map(p => (
            <button key={p.id} type="button" className={css.opcaoProcesso} onClick={() => onEscolher(p)}>
              <FiPackage size={13} /> <strong>{p.nome}</strong>{p.setor ? <em>{p.setor}</em> : null}{!p.ativo ? <em>inativo</em> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModalHistorico({ alvo, onFechar }) {
  const [itens, setItens] = useState(null);
  const [erro, setErro] = useState('');
  useEsc(onFechar);
  useEffect(() => {
    buscarHistoricoRotina(alvo.itemId, alvo.clienteId, alvo.prazo).then(r => setItens(r.items || [])).catch(e => setErro(e.message));
  }, [alvo]);
  return (
    <div className={css.modalFundo}>
      <div className={css.modalCaixa}>
        <div className={css.modalTopo}>
          <div>
            <h3><FiClock size={14} /> Histórico</h3>
            <p>{alvo.cliente} · {alvo.rotina}</p>
          </div>
          <button type="button" className={css.btnFechar} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.modalCorpo}>
          {erro && <p className={css.erro}>{erro}</p>}
          {!itens ? <p className={css.vazio}>Carregando...</p> : itens.length === 0 ? <p className={css.vazio}>Sem registros.</p> : (
            <ul className={css.historico}>
              {itens.map((h, i) => (
                <li key={i} className={h.acao === 'reabriu' ? css.hReabriu : css.hConcluiu}>
                  <strong>{h.acao === 'reabriu' ? 'Reabriu' : 'Concluiu'}</strong>
                  <span>{h.usuario_nome || h.usuario_email || '—'} · {new Date(h.em).toLocaleString('pt-BR')}</span>
                  {h.motivo && <em>{h.acao === 'reabriu' ? `Motivo: ${h.motivo}` : h.motivo}</em>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════ */
export default function VisaoVencimentos() {
  const [competencia, setCompetencia] = useState(competenciaAtual());
  /* o mês acima é de vencimento ou de competência -- lembrado no navegador */
  const [modoMes, setModoMes] = useState(() => {
    try { return localStorage.getItem('vencimentos.modoMes') === 'competencia' ? 'competencia' : 'vencimento'; } catch { return 'vencimento'; }
  });
  /* colunas agrupadas por bloco (como sempre foi) ou na ordem das datas das
     etapas -- aí o bloco se divide quando outro entra no meio (ex.: Guias
     ISS 06 › Declarações PGDAS 12 › Guias DAS 15). Lembrado no navegador. */
  const [ordemColunas, setOrdemColunas] = useState(() => {
    try { return localStorage.getItem('vencimentos.ordemColunas') === 'bloco' ? 'bloco' : 'data'; } catch { return 'data'; }
  });
  function trocarOrdemColunas(o) {
    setOrdemColunas(o);
    try { localStorage.setItem('vencimentos.ordemColunas', o); } catch { /* sem localStorage, só não lembra */ }
  }
  function trocarModoMes(m) {
    setModoMes(m);
    try { localStorage.setItem('vencimentos.modoMes', m); } catch { /* sem localStorage, só não lembra */ }
  }
  const [dados, setDados] = useState({ blocos: [], clientes: [], execucoes: [] });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [busca, setBusca] = useState('');
  /* filtro por processo (aba Processos do menu Rotinas): só as etapas dele
     e, em cada etapa, só os clientes marcados nela dentro do processo */
  const [processoFiltro, setProcessoFiltro] = useState('');
  /* gerente/admin: ver como cada setor enxerga (o funcionário já recebe só o do setor dele) */
  const [setorFiltro, setSetorFiltro] = useState('');
  const [regime, setRegime] = useState('');
  const [kpiFiltro, setKpiFiltro] = useState(null);
  /* concluído some da grade (linha e coluna); o olho mostra de volta */
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);
  const [ordem, setOrdem] = useState({ campo: 'codigo', dir: 1 });
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [reabrindo, setReabrindo] = useState(null);  // bolinha concluída sendo reaberta
  const [vendoHistorico, setVendoHistorico] = useState(null);
  const [vendoParticularidade, setVendoParticularidade] = useState(null);
  const [vendoSemMov, setVendoSemMov] = useState(null);
  const [vendoObs, setVendoObs] = useState(null);
  const [confirmandoObs, setConfirmandoObs] = useState(null);
  /* Empresa e Regime recolhidos: sobra espaço pras rotinas. Lembrado no
     navegador (preferência de quem usa, não dado do sistema). */
  const [recolhido, setRecolhido] = useState(() => {
    try { return localStorage.getItem('vencimentos.recolhido') === '1'; } catch { return false; }
  });
  function alternarRecolhido(e) {
    e.stopPropagation(); // o clique no cabeçalho ordena -- o botão não
    setRecolhido(v => {
      try { localStorage.setItem('vencimentos.recolhido', v ? '0' : '1'); } catch { /* sem localStorage, só não lembra */ }
      return !v;
    });
  }

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    return buscarVencimentosRotinas(competencia, modoMes).then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [competencia, modoMes]);
  useEffect(() => { carregar(); }, [carregar]);

  const hoje = hojeISO();
  /* "itemId|clienteId" -> [{ vigencia, texto }] em ordem de vigência; no
     mês M vale a última com vigência <= M (texto vazio = sem anotação) */
  const particularidades = useMemo(() => {
    const m = new Map();
    [...(dados.particularidades || [])].sort((a, b) => a.vigencia.localeCompare(b.vigencia)).forEach(p => {
      const k = `${p.item_id}|${p.cliente_id}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    return m;
  }, [dados.particularidades]);
  const observacoes = useMemo(() => new Map((dados.observacoes || []).map(r => [`${r.item_id}|${r.cliente_id}|${r.data}`, r])), [dados.observacoes]);
  const obsPendente = (itemId, clienteId, prazo) => { const o = observacoes.get(`${itemId}|${clienteId}|${prazo}`); return o && !o.resolvida ? o : null; };
  const semMovimento = useMemo(() => new Map((dados.semMovimento || []).map(r => [`${r.item_id}|${r.cliente_id}|${r.data}`, r])), [dados.semMovimento]);
  const particularidadeNoMes = (itemId, clienteId, mes) => {
    const lista = (particularidades.get(`${itemId}|${clienteId}`) || []).filter(p => p.vigencia <= mes);
    return lista[lista.length - 1] || null;
  };
  async function gravarParticularidade(alvo, texto) {
    const t = texto.trim();
    await salvarParticularidade(alvo.itemId, alvo.clienteId, alvo.mes, t);
    /* mesma regra do backend: troca a linha desse mês; não grava se repete o que já valia */
    setDados(d => {
      const doPar = (p) => p.item_id === alvo.itemId && p.cliente_id === alvo.clienteId;
      const lista = d.particularidades || [];
      const antes = lista.filter(p => doPar(p) && p.vigencia < alvo.mes).sort((a, b) => a.vigencia.localeCompare(b.vigencia)).pop();
      const resto = lista.filter(p => !(doPar(p) && p.vigencia === alvo.mes));
      return { ...d, particularidades: (antes?.texto || '') === t ? resto : [...resto, { item_id: alvo.itemId, cliente_id: alvo.clienteId, vigencia: alvo.mes, texto: t }] };
    });
    setVendoParticularidade(null);
  }
  const comHistorico = useMemo(
    () => new Set((dados.comHistorico || []).map(h => `${h.item_id}|${h.cliente_id}|${h.data}`)),
    [dados.comHistorico]
  );

  /* marcar/desmarcar: muda na hora e desfaz se o backend recusar */
  /* concluir: marca na hora (e desfaz se o backend recusar). Reabrir: abre
     o modal do motivo; só desmarca depois de confirmado. Os dois ficam no
     histórico -- o ícone do canto aparece na hora pra quem pode ver. */
  function registrarHistoricoLocal(itemId, clienteId, prazo) {
    setDados(d => ({ ...d, comHistorico: [...(d.comHistorico || []), { item_id: itemId, cliente_id: clienteId, data: prazo }] }));
  }
  async function concluir(itemId, clienteId, prazo, contexto) {
    const pend = obsPendente(itemId, clienteId, prazo);
    if (pend) {
      setConfirmandoObs({ itemId, clienteId, prazo, texto: pend.texto, cliente: contexto?.cliente || '', rotina: contexto?.rotina || '',
        acao: () => marcarRotina(itemId, clienteId, prazo, true, undefined, { observacao_resolvida: true }) });
      return;
    }
    setDados(d => ({ ...d, execucoes: [...d.execucoes, { item_id: itemId, cliente_id: clienteId, data: prazo }] }));
    try { await marcarRotina(itemId, clienteId, prazo, true); registrarHistoricoLocal(itemId, clienteId, prazo); }
    catch (e) { setErro(e.message); carregar(); }
  }
  /* editar/visualizar sem sair da aba: tarefa (individual ou do grupo) e
     rotina (o bloco do menu Rotinas) */
  const [editandoTarefa, setEditandoTarefa] = useState(null);
  const [editandoGrupo, setEditandoGrupo] = useState(null);
  const [vendoTarefa, setVendoTarefa] = useState(null);
  const [editandoBloco, setEditandoBloco] = useState(null);
  /* janela de blocos: o que já passou e o período de hoje ficam sempre
     visíveis (é onde pode haver pendência -- linha concluída já some
     sozinha); só os blocos que ainda não chegaram ficam guardados, e a
     seta › revela um por clique */
  const [extraDepois, setExtraDepois] = useState(0);
  const [todosBlocos, setTodosBlocos] = useState(false);
  /* filtro por bloco (ícone no canto do bloco do cabeçalho): só os clientes
     com pendência nas colunas daquele bloco; clicar de novo desliga */
  const [filtroBloco, setFiltroBloco] = useState(null); // { chave, colunas: [chaves] }
  const [filtroEmpresa, setFiltroEmpresa] = useState(null); // id da empresa isolada pelo funil da linha
  /* "Editar" geral: só com ele ligado aparece o lápis em cada coluna */
  const [modoEdicao, setModoEdicao] = useState(false);
  async function tarefasDoDia(data) { return (await listarTarefas({ inicio: data, fim: data })).items || []; }
  async function abrirTarefa(tarefaId, data, modo) {
    setErro('');
    try {
      const t = (await tarefasDoDia(data)).find(x => x.id === tarefaId);
      if (!t) { setErro('Tarefa não encontrada.'); return; }
      if (modo === 'ver') setVendoTarefa(t); else setEditandoTarefa(t);
    } catch (e) { setErro(e.message); }
  }
  /* coluna de tarefa: uma só = edita ela; todas do mesmo grupo (vinculada a
     vários clientes) = edita o grupo; tarefas independentes = pelo quadro */
  async function editarColunaTarefa(c) {
    setErro('');
    try {
      const daColuna = (await tarefasDoDia(c.data)).filter(t => `t:${normalizar(t.titulo)}|${c.data}` === c.chave);
      if (!daColuna.length) { setErro('Tarefa não encontrada.'); return; }
      if (daColuna.length === 1) { setEditandoTarefa(daColuna[0]); return; }
      const grupo = daColuna[0].grupo_id;
      if (grupo && daColuna.every(t => t.grupo_id === grupo)) {
        const todas = (await listarTarefas({})).items || [];
        setEditandoGrupo(todas.filter(t => t.grupo_id === grupo));
        return;
      }
      setErro('Esta coluna junta tarefas independentes de vários clientes: edite pelo quadro de cada cliente (Visualizar › Editar).');
    } catch (e) { setErro(e.message); }
  }
  /* processo (pacote) da rotina: abre o editor do processo aqui mesmo, pra
     desvincular o cliente sem sair da tela. Mais de um processo com a rotina:
     o do filtro, se houver; senão pergunta qual */
  const [editandoProcesso, setEditandoProcesso] = useState(null); // { registro, blocos, clientes }
  const [escolhendoProcesso, setEscolhendoProcesso] = useState(null); // { opcoes, blocos, clientes, rotina }
  async function editarProcessoDaColuna(c) {
    setErro('');
    try {
      const [pacotes, blocos, clientes] = await Promise.all([
        listarPacotesRotina().then(r => r.items || []), listarBlocosRotina().then(r => r.items || []), listarClientes(),
      ]);
      const comRotina = pacotes.filter(p => (p.item_ids || []).includes(c.itemId));
      if (!comRotina.length) { setErro('Esta rotina não está em nenhum processo.'); return; }
      const doFiltro = processoFiltro && comRotina.find(p => p.id === processoFiltro);
      if (doFiltro || comRotina.length === 1) { setEditandoProcesso({ registro: doFiltro || comRotina[0], blocos, clientes }); return; }
      setEscolhendoProcesso({ opcoes: comRotina, blocos, clientes, rotina: c.nome });
    } catch (e) { setErro(e.message); }
  }

  async function editarColunaRotina(c) {
    setErro('');
    try {
      const blocos = (await listarBlocosRotina()).items || [];
      const b = blocos.find(x => x.id === c.blocoId);
      if (!b) { setErro('Rotina não encontrada.'); return; }
      setEditandoBloco({ registro: b, locais: [...new Set(blocos.map(x => x.local).filter(Boolean))].sort() });
    } catch (e) { setErro(e.message); }
  }

  /* tarefa: a bolinha conclui/reabre a própria tarefa (recorrência mensal
     gera a próxima no backend, como na aba Tarefas) */
  async function alternarTarefa(tarefaId, concluir) {
    setErro('');
    setDados(d => ({ ...d, tarefas: (d.tarefas || []).map(t => (t.id === tarefaId ? { ...t, concluido: concluir } : t)) }));
    try { await atualizarTarefa(tarefaId, { concluido: concluir }); }
    catch (e) { setErro(e.message); carregar(); }
  }
  async function reabrir(alvo, motivo) {
    await marcarRotina(alvo.itemId, alvo.clienteId, alvo.prazo, false, motivo);
    const chave = (e) => e.item_id === alvo.itemId && e.cliente_id === alvo.clienteId && e.data === alvo.prazo;
    setDados(d => ({ ...d, execucoes: d.execucoes.filter(e => !chave(e)) }));
    registrarHistoricoLocal(alvo.itemId, alvo.clienteId, alvo.prazo);
    setReabrindo(null);
  }

  /* ── junta tudo: colunas por período + uma linha por cliente ── */
  const { grupos, linhas } = useMemo(() => {
    const colunasMap = new Map(); // chave -> coluna
    const linhasMap = new Map();  // clienteId -> linha
    /* conclusão é por ocorrência: rotina x cliente x prazo */
    const feitas = new Set(dados.execucoes.map(e => `${e.item_id}|${e.cliente_id}|${e.data}`));
    const novaLinha = (c) => ({
      id: c.id, codigo: c.codigo, nome: c.nome, niboId: c.nibo_customer_id || c.niboCustomerId || null,
      regime: regimeDo(c), locais: new Set(), celulas: new Map(),
      /* criado na aprovação do lead: aparece, mas só marca depois do onboard */
      bloqueado: !!c.onboard_pendente,
    });
    dados.clientes.forEach(c => linhasMap.set(c.id, novaLinha(c)));
    const proc = processoFiltro ? (dados.processos || []).find(p => p.id === processoFiltro) : null;
    const noProcesso = proc && { itens: new Set(proc.itens), pares: new Set(proc.vinculos) };

    /* cada bloco já vem com as ocorrências do mês (semanal = uma por
       semana); data = dia próprio da rotina (pode não ter), prazo = data
       ou o fim do período */
    dados.blocos.forEach(b => b.ocorrencias.forEach(o => {
      const periodo = `${o.inicio}|${o.fim}`;
      o.itens.forEach(it => {
        if (noProcesso && !noProcesso.itens.has(it.id)) return;
        const chave = `r:${it.id}|${it.prazo}`;
        colunasMap.set(chave, {
          chave, periodo, inicio: o.inicio, fim: o.fim, data: it.data, prazo: it.prazo, origem: 'rotina', nome: it.nome,
          local: b.local, titulo: b.titulo, blocoId: b.id, itemId: it.id, ordem: it.ordem ?? 0, competencia: o.competencia || null, setores: it.setores || [],
        });
        /* clientes marcados NESTA rotina no pacote (não no bloco todo) */
        (it.cliente_ids || []).forEach(cid => {
          if (noProcesso && !noProcesso.pares.has(`${it.id}|${cid}`)) return;
          const linha = linhasMap.get(cid);
          if (!linha) return; // cliente inativo
          if (b.local) linha.locais.add(b.local);
          const concluido = feitas.has(`${it.id}|${cid}|${it.prazo}`);
          linha.celulas.set(chave, {
            estado: concluido ? { tipo: 'concluido' } : estadoPorPrazo(hoje, it.prazo),
            nome: it.nome, local: b.local, data: it.data, prazo: it.prazo, itemId: it.id, concluido,
          });
        });
      });
    }));

    /* tarefas: uma coluna por título + dia (o bloco do cabeçalho é "Tarefa"
       naquele dia); sem cliente vai numa linha à parte. No filtro por
       processo elas não entram (não fazem parte de processo). */
    if (!noProcesso) {
      (dados.tarefas || []).forEach(t => {
        if (!t.data) return;
        const chave = `t:${normalizar(t.titulo)}|${t.data}`;
        if (!colunasMap.has(chave)) {
          colunasMap.set(chave, {
            chave, periodo: `${t.data}|${t.data}`, inicio: t.data, fim: t.data, data: t.data, prazo: t.data, origem: 'tarefa',
            nome: t.titulo, local: 'Tarefa', titulo: '', blocoId: `tarefa:${t.data}`, ordem: 0, competencia: null,
            setores: t.setor ? [t.setor] : [],
          });
        }
        const idLinha = t.cliente_id || 'sem-cliente';
        if (!linhasMap.has(idLinha)) {
          if (t.cliente_id) return; // cliente fora da lista (inativo)
          linhasMap.set(idLinha, { id: idLinha, codigo: '—', nome: 'Tarefas sem cliente', niboId: null, regime: '—', locais: new Set(), celulas: new Map(), bloqueado: false });
        }
        const linha = linhasMap.get(idLinha);
        const estado = t.concluido ? { tipo: 'concluido' } : estadoPorPrazo(hoje, t.data);
        const atual = linha.celulas.get(chave);
        /* duas tarefas iguais no mesmo dia pro mesmo cliente: vale a pendente */
        if (!atual || (atual.concluido && !t.concluido)) {
          linha.celulas.set(chave, { estado, nome: t.titulo, local: 'Tarefa', data: t.data, prazo: t.data, tarefaId: t.id, concluido: t.concluido, responsavel: t.responsavel });
        }
      });
    }

    linhasMap.forEach(l => {
      const est = [...l.celulas.values()].map(c => c.estado.tipo);
      l.cont = est.reduce((m, t) => { m[t] = (m[t] || 0) + 1; return m; }, {});
      l.total = est.length;
      l.feitas = l.cont.concluido || 0;
    });

    /* colunas agrupadas pelo período do bloco, na ordem das datas; dentro
       do período, pela data individual */
    const porPeriodo = new Map();
    [...colunasMap.values()].forEach(c => {
      if (!porPeriodo.has(c.periodo)) porPeriodo.set(c.periodo, { periodo: c.periodo, inicio: c.inicio, fim: c.fim, colunas: [] });
      porPeriodo.get(c.periodo).colunas.push(c);
    });
    const ordenados = [...porPeriodo.values()]
      .sort((a, b) => a.inicio.localeCompare(b.inicio) || a.fim.localeCompare(b.fim))
      .map(g => {
        /* rotinas do mesmo bloco ficam juntas (o local/título cobre todas);
           blocos na ordem do primeiro prazo; dentro do bloco, pelo prazo */
        const primeiro = new Map();
        g.colunas.forEach(c => { if (!primeiro.has(c.blocoId) || c.prazo < primeiro.get(c.blocoId)) primeiro.set(c.blocoId, c.prazo); });
        return { ...g, colunas: g.colunas.sort((a, b) => primeiro.get(a.blocoId).localeCompare(primeiro.get(b.blocoId))
          || (a.blocoId || '').localeCompare(b.blocoId || '') || a.prazo.localeCompare(b.prazo) || a.ordem - b.ordem) };
      });
    return { grupos: ordenados, linhas: [...linhasMap.values()].filter(l => l.total > 0) };
  }, [dados, hoje, processoFiltro]);

  const colunasPlanas = grupos.flatMap(g => g.colunas);
  const regimes = [...new Set(linhas.map(l => l.regime))].filter(r => r !== '—')
    .sort((a, b) => ORDEM_REGIME.indexOf(a) - ORDEM_REGIME.indexOf(b));

  /* com filtro de setor, só as colunas que batem (etapa sem setor aparece
     pra todos os setores); o de processo já vem aplicado na montagem */
  const colunaNoFiltro = (c) => (!setorFiltro || !c.setores?.length || c.setores.includes(setorFiltro));
  /* ── filtros ── */
  const q = normalizar(busca.trim());
  const filtradas = linhas.filter(l => {
    if (filtroEmpresa && l.id !== filtroEmpresa) return false;
    if (q && !normalizar(`${l.codigo} ${l.nome}`).includes(q)) return false;
    if (setorFiltro && !colunasPlanas.some(c => colunaNoFiltro(c) && l.celulas.has(c.chave))) return false;
    if (regime && l.regime !== regime) return false;
    if (kpiFiltro === 'vencido' && !l.cont.vencido) return false;
    if (kpiFiltro === 'hoje' && !l.cont.hoje) return false;
    if (kpiFiltro === 'proximo' && !l.cont.proximo) return false;
    if (kpiFiltro === 'prazo' && !l.cont.prazo) return false;
    return true;
  });

  /* KPIs contam rotinas (bolinhas) das empresas filtradas */
  const k = filtradas.reduce((m, l) => {
    Object.entries(l.cont).forEach(([t, n]) => { m[t] = (m[t] || 0) + n; });
    m.total += l.total; return m;
  }, { total: 0 });
  const pct = (n) => (k.total ? `${Math.round(((n || 0) / k.total) * 100)}%` : '0%');
  const KPIS = [
    { id: 'vencido', titulo: 'Vencidos', valor: k.vencido || 0, icone: FiAlertOctagon, cor: css.kVermelho },
    { id: 'hoje', titulo: 'Hoje', valor: k.hoje || 0, icone: FiClock, cor: css.kLaranja },
    { id: 'proximo', titulo: 'Próximos 3 dias', valor: k.proximo || 0, icone: FiAlertCircle, cor: css.kLaranja },
    { id: 'prazo', titulo: 'No prazo', valor: k.prazo || 0, icone: FiCheckCircle, cor: css.kVerde },
  ];

  /* linha 100% feita sai da grade; "100% concluídas" no filtro mostra */
  const linhaConcluida = (l) => l.total > 0 && l.feitas === l.total;
  const qtdLinhasConcluidas = filtradas.filter(linhaConcluida).length;
  const verConcluidos = mostrarConcluidos;
  const linhasTabela = verConcluidos ? filtradas : filtradas.filter(l => !linhaConcluida(l));

  /* coluna sem nenhuma pendência entre as empresas filtradas também sai */
  const colunaConcluida = (c) => filtradas.every(l => { const cel = l.celulas.get(c.chave); return !cel || cel.estado.tipo === 'concluido'; });
  const gruposPorBloco = grupos
    .map(g => ({ ...g, chave: g.periodo, colunas: g.colunas.filter(c => colunaNoFiltro(c) && (verConcluidos || !colunaConcluida(c))) }))
    .filter(g => g.colunas.length);
  /* ordem por data: todas as colunas visíveis pela data da etapa (no mesmo
     dia, o bloco que começa antes vem antes) e cada sequência do mesmo
     bloco vira um grupo -- o cabeçalho do bloco se repete se ele voltar */
  const gruposTodos = ordemColunas === 'bloco' ? gruposPorBloco : (() => {
    const cols = gruposPorBloco.flatMap(g => g.colunas).sort((a, b) => a.prazo.localeCompare(b.prazo)
      || a.inicio.localeCompare(b.inicio) || (a.blocoId || '').localeCompare(b.blocoId || '') || a.ordem - b.ordem);
    const saida = [];
    cols.forEach(c => {
      const ult = saida[saida.length - 1];
      if (ult && ult.blocoId === c.blocoId) ult.colunas.push(c);
      else saida.push({ chave: `${c.periodo}#${saida.length}`, periodo: c.periodo, inicio: c.inicio, fim: c.fim, blocoId: c.blocoId, colunas: [c] });
    });
    return saida;
  })();
  /* janela: do primeiro bloco até o último que contém hoje (sem nenhum
     com hoje: até o primeiro que ainda vai começar; mês que já passou:
     tudo). Os que ainda não chegaram ficam guardados atrás da seta › */
  const { gruposVisiveis, ocultosDepois } = (() => {
    const n = gruposTodos.length;
    if (!n || todosBlocos) return { gruposVisiveis: gruposTodos, ocultosDepois: 0 };
    const comHoje = gruposTodos.map((g, i) => (g.inicio <= hoje ? i : -1)).filter(i => i >= 0);
    let fim;
    if (gruposTodos.some(g => g.inicio <= hoje && hoje <= g.fim)) fim = comHoje[comHoje.length - 1];
    else { const prox = gruposTodos.findIndex(g => g.inicio > hoje); fim = prox >= 0 ? prox : n - 1; }
    fim = Math.min(n - 1, fim + extraDepois);
    return { gruposVisiveis: gruposTodos.slice(0, fim + 1), ocultosDepois: n - 1 - fim };
  })();
  const janelaAberta = todosBlocos || extraDepois > 0;
  const colunasDaJanela = new Set(gruposVisiveis.flatMap(g => g.colunas.map(c => c.chave)));
  const qtdColunasVisiveis = gruposVisiveis.reduce((n, g) => n + g.colunas.length, 0);
  /* por período: os blocos (sequências de colunas do mesmo bloco) e a largura
     mínima de cada coluna pra o local/título/competência caber em 2 linhas */
  const blocosPorPeriodo = new Map();
  const minimoColuna = new Map();
  gruposVisiveis.forEach(g => {
    const lista = [];
    g.colunas.forEach(c => {
      const ult = lista[lista.length - 1];
      if (ult && ult.blocoId === c.blocoId) { ult.n += 1; ult.colunas.push(c.chave); }
      else lista.push({ blocoId: c.blocoId, local: c.local, titulo: c.titulo, competencia: c.competencia, n: 1, colunas: [c.chave] });
    });
    lista.forEach(b => { const m = minimoPorColuna(b); b.colunas.forEach(k => minimoColuna.set(k, m)); });
    blocosPorPeriodo.set(g.chave, lista);
  });
  const qtdColunasOcultas = colunasPlanas.filter(colunaNoFiltro).length - qtdColunasVisiveis;

  const linhasDaJanela = linhasTabela.filter(l => [...l.celulas.keys()].some(k => colunasDaJanela.has(k)))
    .filter(l => !filtroBloco || filtroBloco.colunas.some(k => {
      const cel = l.celulas.get(k);
      return cel && (verConcluidos || cel.estado.tipo !== 'concluido');
    }));
  const ordenadas = [...linhasDaJanela].sort((a, b) => {
    const v = (l) => (ordem.campo === 'nome' ? l.nome : ordem.campo === 'regime' ? l.regime : l.codigo) || '';
    return v(a).localeCompare(v(b), 'pt-BR', { numeric: true }) * ordem.dir;
  });
  const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const visiveis = ordenadas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina);

  useEffect(() => { setPagina(1); }, [busca, processoFiltro, setorFiltro, regime, kpiFiltro, competencia, modoMes, porPagina, mostrarConcluidos]);
  /* mudou o mês, o modo ou o agrupamento: volta pra janela de hoje */
  useEffect(() => { setExtraDepois(0); setTodosBlocos(false); setFiltroBloco(null); }, [competencia, modoMes, ordemColunas, processoFiltro]);
  useEffect(() => { setPagina(1); }, [filtroBloco, filtroEmpresa]);

  function ordenarPor(campo) {
    setOrdem(o => (o.campo === campo ? { campo, dir: -o.dir } : { campo, dir: 1 }));
  }
  const Seta = ({ campo }) => (ordem.campo !== campo ? <span className={css.setaInativa}>↕</span>
    : ordem.dir === 1 ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />);

  function limpar() {
    setBusca(''); setProcessoFiltro(''); setSetorFiltro(''); setRegime(''); setKpiFiltro(null); setFiltroEmpresa(null);
  }

  return (
    <div className={css.page}>
      <div className={css.kpis}>
        {KPIS.map(c => {
          const Icone = c.icone; const ativo = kpiFiltro === c.id;
          return (
            <button key={c.id} type="button" className={`${css.kpi} ${ativo ? css.kpiAtivo : ''}`}
              onClick={() => setKpiFiltro(ativo ? null : c.id)}>
              <span className={`${css.kpiIcone} ${c.cor}`}><Icone size={18} /></span>
              <span>
                <span className={css.kpiTitulo}>{c.titulo}</span>
                <span className={css.kpiLinha}><strong className={c.cor}>{c.valor}</strong> <small>({pct(c.valor)})</small></span>
              </span>
            </button>
          );
        })}
        <div className={css.kpi}>
          <span className={`${css.kpiIcone} ${css.kCinza}`}><FiClipboard size={18} /></span>
          <span>
            <span className={css.kpiTitulo}>Total de rotinas</span>
            <span className={css.kpiLinha}><strong>{k.total}</strong> <small>{k.concluido || 0} feitas</small></span>
          </span>
        </div>
      </div>

      <div className={css.filtros}>
        {recolhido && (
          <div className={css.buscaWrap}>
            <FiSearch size={14} />
            <input placeholder="Buscar código, empresa ou cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
        )}
        {dados.podeVerHistorico && (
          <select value={setorFiltro} onChange={e => setSetorFiltro(e.target.value)} title="Ver como cada setor enxerga esta tela">
            <option value="">Todos os setores</option>
            {SETORES_ROTINA.map(st => <option key={st.v} value={st.v}>{st.l}</option>)}
          </select>
        )}
        <select value={processoFiltro} onChange={e => setProcessoFiltro(e.target.value)}>
          <option value="">Todos os processos</option>
          {(dados.processos || []).map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
        <select value={regime} onChange={e => setRegime(e.target.value)}>
          <option value="">Todos os regimes</option>
          {regimes.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="button" className={css.iconeFiltro} onClick={limpar} title="Limpar filtros" aria-label="Limpar filtros">
          <FiFilter size={15} /><FiX size={10} className={css.iconeFiltroX} />
        </button>
        <button type="button" className={`${css.btnEditarMestre} ${modoEdicao ? css.btnEditarMestreAtivo : ''}`}
          onClick={() => setModoEdicao(v => !v)}
          title={modoEdicao ? 'Fechar a edição' : 'Editar: mostra o lápis em cada coluna pra abrir a rotina ou a tarefa'}>
          {modoEdicao ? <><FiCheck size={13} /> Concluir edição</> : <><FiEdit2 size={13} /> Editar</>}
        </button>
        <button type="button" className={`${css.iconeFiltro} ${verConcluidos ? css.iconeFiltroAtivo : ''}`}
          onClick={() => setMostrarConcluidos(v => !v)}
          aria-label={verConcluidos ? 'Ocultar concluídos' : 'Ver concluídos'}
          title={verConcluidos ? 'Ocultar o que já está 100% concluído' : `Ver concluídos: ${qtdLinhasConcluidas} empresas e ${qtdColunasOcultas} colunas ocultas`}>
          {verConcluidos ? <FiEyeOff size={15} /> : <FiEye size={15} />}
        </button>
        <div className={css.ordemColunas}>
          <button type="button" className={ordemColunas === 'bloco' ? css.ordemAtiva : ''} onClick={() => trocarOrdemColunas('bloco')}
            title="Colunas agrupadas por bloco (Guias inteiro, depois Declarações...)">Agrupar por bloco</button>
          <button type="button" className={ordemColunas === 'data' ? css.ordemAtiva : ''} onClick={() => trocarOrdemColunas('data')}
            title="Colunas na ordem das datas das etapas; o bloco se divide quando outro entra no meio">Ordem por data</button>
        </div>
        <div className={css.competencia}>
          <div className={css.modoMes}>
            <button type="button" className={modoMes === 'vencimento' ? css.modoMesAtivo : ''} onClick={() => trocarModoMes('vencimento')}
              title="O mês escolhido é o do vencimento">Vencimento</button>
            <button type="button" className={modoMes === 'competencia' ? css.modoMesAtivo : ''} onClick={() => trocarModoMes('competencia')}
              title="O mês escolhido é a competência das informações (ex.: folha de 10/2026, mesmo vencendo em 11/2026)">Competência</button>
          </div>
          <button type="button" onClick={() => setCompetencia(c => deslocar(c, -1))}><FiChevronLeft size={15} /></button>
          <span>{competencia.slice(5)}/{competencia.slice(0, 4)}</span>
          <button type="button" onClick={() => setCompetencia(c => deslocar(c, 1))}><FiChevronRight size={15} /></button>
        </div>
      </div>

      {erro && <p className={css.erro}>{erro}</p>}

      <div className={css.tabelaWrap}>
        {carregando ? (
          <p className={css.vazio}>Carregando...</p>
        ) : !colunasPlanas.length ? (
          <p className={css.vazio}>Nenhuma rotina neste mês. Cadastre em Obrigações › Rotinas.</p>
        ) : (
          <table className={css.tabela}>
            <thead>
              <tr>
                <th rowSpan={3} className={`${css.fixa} ${css.fCodigo} ${recolhido ? css.fCodigoSo : css.cantoComBusca}`} onClick={() => ordenarPor('codigo')}>
                  {/* busca no espaço livre do canto (acima dos títulos); recolhido, volta pra barra de cima */}
                  {!recolhido && (
                    <div className={css.buscaCanto} onClick={e => e.stopPropagation()}>
                      <FiSearch size={13} />
                      <input placeholder="Buscar código, empresa ou cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
                      {busca && <button type="button" title="Limpar busca" onClick={() => setBusca('')}><FiX size={12} /></button>}
                    </div>
                  )}
                  Cód. <Seta campo="codigo" />
                  <button type="button" className={css.btnRecolher} onClick={alternarRecolhido}
                    title={recolhido ? 'Mostrar empresa e regime' : 'Recolher empresa e regime (mais espaço pras rotinas)'}>
                    {recolhido ? <FiChevronsRight size={12} /> : <FiChevronsLeft size={12} />}
                  </button>
                </th>
                {!recolhido && <th rowSpan={3} className={`${css.fixa} ${css.fEmpresa} ${css.cantoComBusca}`} onClick={() => ordenarPor('nome')}>Empresa / Cliente <Seta campo="nome" /></th>}
                {!recolhido && <th rowSpan={3} className={`${css.fixa} ${css.fRegime} ${css.cantoComBusca}`} onClick={() => ordenarPor('regime')}>Regime <Seta campo="regime" /></th>}
                {gruposVisiveis.map((g, i) => {
                  const dias = diasEntre(hoje, g.fim);
                  const cls = dias < 0 ? css.dPassada : dias <= 3 ? css.dProxima : '';
                  return (
                    <th key={g.chave} colSpan={g.colunas.length} className={`${css.thData} ${cls} ${i % 2 ? css.alt : ''}`}>
                      {textoPeriodo(g.inicio, g.fim)}
                    </th>
                  );
                })}
                {(ocultosDepois > 0 || janelaAberta) && (
                  <th rowSpan={3} className={`${css.thJanela} ${css.thJanelaDireita}`}>
                    {ocultosDepois > 0 && (
                      <button type="button" title={`Mostrar o próximo bloco (${ocultosDepois} guardado${ocultosDepois > 1 ? 's' : ''})`}
                        onClick={() => setExtraDepois(v => v + 1)}><FiChevronRight size={15} /><small>{ocultosDepois}</small></button>
                    )}
                    {ocultosDepois > 0 && (
                      <button type="button" className={css.janelaTodos} title="Mostrar todos os blocos do mês" onClick={() => setTodosBlocos(true)}>todos</button>
                    )}
                    {janelaAberta && (
                      <button type="button" className={css.janelaTodos} title="Voltar só pros blocos de hoje"
                        onClick={() => { setExtraDepois(0); setTodosBlocos(false); }}>hoje</button>
                    )}
                  </th>
                )}
              </tr>
              {/* local + título uma vez só, cobrindo as rotinas do bloco */}
              <tr>
                {gruposVisiveis.map((g, i) => {
                  const blocosDoPeriodo = blocosPorPeriodo.get(g.chave) || [];
                  return blocosDoPeriodo.map(b => {
                    const chaveBloco = `${g.chave}|${b.blocoId}`;
                    const ativo = filtroBloco?.chave === chaveBloco;
                    return (
                    <th key={chaveBloco} colSpan={b.n} className={`${css.thBloco} ${i % 2 ? css.alt : ''} ${ativo ? css.thBlocoFiltrado : ''}`}
                      title={[b.local, b.titulo, b.competencia && `competência ${b.competencia.slice(5)}/${b.competencia.slice(0, 4)}`].filter(Boolean).join(' · ')}>
                      {/* filtra os clientes que têm pendência neste bloco */}
                      <button type="button" className={`${css.filtroBlocoBtn} ${ativo ? css.filtroBlocoAtivo : ''}`}
                        title={ativo ? 'Mostrar todos os clientes de novo' : 'Mostrar só os clientes com pendência neste bloco'}
                        onClick={() => setFiltroBloco(ativo ? null : { chave: chaveBloco, colunas: b.colunas })}>
                        <FiFilter size={9} />
                      </button>
                      {/* sempre no máximo 2 linhas: local em cima; título + competência
                          embaixo, sem quebrar (as colunas crescem por igual se precisar) */}
                      {b.local && b.titulo && <div className={css.blocoLinha1}><span className={css.blocoLocal}>{b.local}</span></div>}
                      <div className={css.blocoLinha2}>
                        {b.titulo
                          ? <span className={css.blocoTitulo}>{b.titulo}</span>
                          : b.local && <span className={css.blocoLocal}>{b.local}</span>}
                        {/* competência das informações, em destaque (ex.: 10/26) */}
                        {b.competencia && <span className={css.blocoCompetencia}>{b.competencia.slice(5)}/{b.competencia.slice(2, 4)}</span>}
                      </div>
                    </th>
                    );
                  });
                })}
              </tr>
              <tr>
                {gruposVisiveis.map((g, i) => g.colunas.map(c => {
                  /* a cor da data diz o prazo: verde no prazo, laranja perto, vermelho vencida */
                  const dias = diasEntre(hoje, c.prazo);
                  const corData = dias < 0 ? css.dataVencida : dias <= 3 ? css.dataProxima : css.dataPrazo;
                  return (
                    <th key={c.chave} className={`${css.thItem} ${i % 2 ? css.alt : ''}`} style={{ minWidth: minimoColuna.get(c.chave) }}
                      title={[c.local, c.titulo, c.nome, c.data ? dataCurta(c.data) : `até ${dataCurta(c.prazo)}`].filter(Boolean).join(' · ')}>
                      {/* lápis só no modo edição (botão "Editar" da barra): edita sem sair daqui */}
                      {modoEdicao && (
                        <span className={css.thAcoes}>
                          {c.origem !== 'tarefa' && (
                            <button type="button" title="Editar o processo desta rotina (vincular/desvincular clientes)"
                              onClick={() => editarProcessoDaColuna(c)}><FiPackage size={10} /></button>
                          )}
                          <button type="button" title={c.origem === 'tarefa' ? 'Editar tarefa' : 'Editar rotina'}
                            onClick={() => (c.origem === 'tarefa' ? editarColunaTarefa(c) : editarColunaRotina(c))}><FiEdit2 size={10} /></button>
                        </span>
                      )}
                      <span>{c.nome}</span>
                      {/* data individual só quando a rotina tem dia próprio */}
                      {c.origem === 'tarefa'
                        ? <em className={css.tagTarefa}>TAREFA {dataCurta(c.data)}</em>
                        : c.data && <em className={corData}>{dataCurta(c.data)}</em>}
                    </th>
                  );
                }))}
              </tr>
            </thead>
            <tbody>
              {visiveis.length === 0 ? (
                <tr><td colSpan={(recolhido ? 1 : 3) + qtdColunasVisiveis + (ocultosDepois > 0 || janelaAberta ? 1 : 0)} className={css.vazio}>
                  {filtradas.length ? 'Tudo concluído por aqui. Clique em "Concluídos" pra ver.' : 'Nenhuma empresa com esses filtros.'}
                </td></tr>
              ) : visiveis.map(l => {
                /* funil da linha (como o do bloco): mostra só esta empresa; clicar de novo volta todas */
                const isolada = filtroEmpresa === l.id;
                const funil = (
                  <button type="button" className={`${css.filtroBlocoBtn} ${css.filtroEmpresaBtn} ${isolada ? css.filtroBlocoAtivo : ''}`}
                    title={isolada ? 'Mostrar todas as empresas de novo' : 'Mostrar só esta empresa'}
                    onClick={() => setFiltroEmpresa(isolada ? null : l.id)}>
                    <FiFilter size={9} />
                  </button>
                );
                return (
                <tr key={l.id} className={isolada ? css.linhaIsolada : ''}>
                  <td className={`${css.fixa} ${css.fCodigo} ${css.codigo} ${recolhido ? css.fCodigoSo : ''}`}
                    title={recolhido ? `${l.nome} · ${l.regime} · ${l.feitas}/${l.total} concluídas` : undefined}>{recolhido && funil}{l.codigo}</td>
                  {!recolhido && (
                    <td className={`${css.fixa} ${css.fEmpresa}`}>
                      {funil}
                      <div className={css.empresa} title={l.bloqueado ? `${l.nome} — bloqueado até concluir o onboard` : l.nome}>
                        {l.bloqueado && <FiLock size={11} className={css.cadeado} />}{l.nome}
                      </div>
                      <div className={css.empresaSub}>{l.feitas}/{l.total} concluídas{l.locais.size ? ` · ${[...l.locais].join(', ')}` : ''}</div>
                    </td>
                  )}
                  {!recolhido && (
                    <td className={`${css.fixa} ${css.fRegime}`}>
                      <span className={`${css.regime} ${css[CLASSE_REGIME[l.regime]] || ''}`}>{l.regime}</span>
                    </td>
                  )}
                  {gruposVisiveis.map((g, i) => g.colunas.map(c => {
                    const cel = l.celulas.get(c.chave);
                    const prazo = cel && cel.estado.dias != null
                      ? ` (${cel.estado.dias < 0 ? `há ${-cel.estado.dias}d` : cel.estado.dias === 0 ? 'hoje' : `em ${cel.estado.dias}d`})` : '';
                    const titulo = cel ? `${cel.nome}${cel.local ? ` · ${cel.local}` : ''} · ${cel.data ? dataCurta(cel.data) : `até ${dataCurta(cel.prazo)}`} — ${TEXTO_ESTADO[cel.estado.tipo]}${prazo}${l.bloqueado ? '\nBloqueado até concluir o onboard' : (cel.itemId || cel.tarefaId) ? `${cel.responsavel ? `\nResponsável: ${cel.responsavel}` : ''}\nClique para marcar/desmarcar` : ''}` : '';
                    const sm = cel?.itemId ? semMovimento.get(`${cel.itemId}|${l.id}|${cel.prazo}`) : null;
                    const tituloCel = sm ? `${titulo}\nSem movimento${sm.precisa_declaracao ? (cel.concluido ? ' — declaração enviada' : ' — declaração a enviar') : ''}` : titulo;
                    return (
                      <td key={c.chave} className={`${css.cel} ${i % 2 ? css.alt : ''}`}>
                        <Bolinha estado={cel?.estado} titulo={tituloCel}
                          onClick={cel?.tarefaId && !l.bloqueado ? () => alternarTarefa(cel.tarefaId, !cel.concluido)
                            : cel?.itemId && !l.bloqueado ? () => (cel.concluido
                            ? setReabrindo({ itemId: cel.itemId, clienteId: l.id, prazo: cel.prazo, cliente: `${l.codigo} - ${l.nome}`, rotina: cel.nome })
                            : concluir(cel.itemId, l.id, cel.prazo, { cliente: `${l.codigo} - ${l.nome}`, rotina: cel.nome })) : undefined} />
                        {/* orelha (canto de cima à esquerda): particularidades da etapa pra
                            esse cliente -- apagada sem texto, destacada com texto */}
                        {cel?.itemId && (() => {
                          const mes = cel.prazo.slice(0, 7);
                          const txt = particularidadeNoMes(cel.itemId, l.id, mes)?.texto;
                          return (
                            <button type="button" className={`${css.orelha} ${txt ? css.orelhaCom : ''}`}
                              title={txt ? `Particularidades: ${txt}` : 'Anotar particularidades desta etapa'}
                              onClick={() => setVendoParticularidade({ itemId: cel.itemId, clienteId: l.id, mes, cliente: `${l.codigo} - ${l.nome}`, rotina: cel.nome })} />
                          );
                        })()}
                        {/* orelha de cima à direita: observação só deste mês, a resolver antes de concluir */}
                        {cel?.itemId && (() => {
                          const ob = observacoes.get(`${cel.itemId}|${l.id}|${cel.prazo}`);
                          return (
                            <button type="button" className={`${css.orelhaDir} ${ob ? (ob.resolvida ? css.orelhaDirOk : css.orelhaDirCom) : ''}`}
                              title={ob ? `${ob.resolvida ? 'Observação resolvida' : 'Observação pendente'}: ${ob.texto}` : 'Anotar observação (só deste mês, a resolver antes de concluir)'}
                              onClick={() => setVendoObs({ itemId: cel.itemId, clienteId: l.id, prazo: cel.prazo, cliente: `${l.codigo} - ${l.nome}`, rotina: cel.nome })} />
                          );
                        })()}
                        {/* orelha de baixo: "sem movimento" no mês -- apagada sem, destacada com */}
                        {cel?.itemId && !l.bloqueado && (
                          <button type="button" className={`${css.orelhaInf} ${sm ? (sm.precisa_declaracao && !cel.concluido ? css.orelhaInfPend : css.orelhaInfCom) : ''}`}
                            title={sm ? (sm.precisa_declaracao ? (cel.concluido ? 'Sem movimento — declaração enviada' : 'Sem movimento — declaração a enviar') : 'Sem movimento — baixada sem declaração') : 'Marcar sem movimento no mês'}
                            onClick={() => setVendoSemMov({ itemId: cel.itemId, clienteId: l.id, prazo: cel.prazo, cliente: `${l.codigo} - ${l.nome}`, rotina: cel.nome, concluido: cel.concluido })} />
                        )}
                        {/* tarefa: "Visualizar" ao passar o mouse abre os detalhes dela */}
                        {cel?.tarefaId && (
                          <button type="button" className={css.celVer} title="Visualizar tarefa"
                            onClick={() => abrirTarefa(cel.tarefaId, cel.data, 'ver')}><FiEye size={10} /></button>
                        )}
                        {/* ícone do histórico: absoluto no canto, não mexe no tamanho */}
                        {dados.podeVerHistorico && cel?.itemId && comHistorico.has(`${cel.itemId}|${l.id}|${cel.prazo}`) && (
                          <button type="button" className={css.iconeHistorico} title="Ver histórico"
                            onClick={() => setVendoHistorico({ itemId: cel.itemId, clienteId: l.id, prazo: cel.prazo, cliente: `${l.codigo} - ${l.nome}`, rotina: cel.nome })}>
                            <FiClock size={8} />
                          </button>
                        )}
                      </td>
                    );
                  }))}
                  {(ocultosDepois > 0 || janelaAberta) && <td className={`${css.celJanela} ${css.celJanelaDireita}`} />}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {vendoTarefa && (
        <ModalTarefaDetalhe tarefa={vendoTarefa} onClose={() => setVendoTarefa(null)}
          onEditar={() => { setEditandoTarefa(vendoTarefa); setVendoTarefa(null); }} />
      )}
      {editandoTarefa && (
        <DrawerTarefa tarefa={editandoTarefa} onClose={() => setEditandoTarefa(null)}
          onSalva={() => { setEditandoTarefa(null); carregar(); }} />
      )}
      {editandoGrupo && (
        <DrawerEditarGrupo itens={editandoGrupo} onClose={() => setEditandoGrupo(null)}
          onSalva={() => { setEditandoGrupo(null); carregar(); }} />
      )}
      {escolhendoProcesso && (
        <ModalEscolherProcesso dados={escolhendoProcesso}
          onEscolher={(p) => { setEditandoProcesso({ registro: p, blocos: escolhendoProcesso.blocos, clientes: escolhendoProcesso.clientes }); setEscolhendoProcesso(null); }}
          onFechar={() => setEscolhendoProcesso(null)} />
      )}
      {editandoProcesso && (
        <DrawerPacote registro={editandoProcesso.registro} blocos={editandoProcesso.blocos} clientes={editandoProcesso.clientes}
          onFechar={() => setEditandoProcesso(null)} onSalvo={() => { setEditandoProcesso(null); carregar(); }} />
      )}
      {editandoBloco && (
        <DrawerBloco registro={editandoBloco.registro} locais={editandoBloco.locais} onFechar={() => setEditandoBloco(null)}
          onSalvo={() => { setEditandoBloco(null); carregar(); }} />
      )}
      {reabrindo && <ModalReabrir alvo={reabrindo} onConfirmar={(m) => reabrir(reabrindo, m)} onFechar={() => setReabrindo(null)} />}
      {vendoHistorico && <ModalHistorico alvo={vendoHistorico} onFechar={() => setVendoHistorico(null)} />}
      {vendoObs && (
        <ModalObservacao alvo={vendoObs} obs={observacoes.get(`${vendoObs.itemId}|${vendoObs.clienteId}|${vendoObs.prazo}`)}
          onSalvar={async (t) => { await salvarObservacao(vendoObs.itemId, vendoObs.clienteId, vendoObs.prazo, t); setVendoObs(null); carregar(); }}
          onFechar={() => setVendoObs(null)} />
      )}
      {confirmandoObs && (
        <ModalConfirmarObservacao alvo={confirmandoObs}
          onSim={async () => { await confirmandoObs.acao(); setConfirmandoObs(null); carregar(); }}
          onFechar={() => setConfirmandoObs(null)} />
      )}
      {vendoSemMov && (
        <ModalSemMovimento alvo={vendoSemMov}
          marcado={semMovimento.get(`${vendoSemMov.itemId}|${vendoSemMov.clienteId}|${vendoSemMov.prazo}`)}
          concluido={vendoSemMov.concluido}
          onMarcar={async (precisa) => {
            const alvo = vendoSemMov;
            const pend = !precisa && obsPendente(alvo.itemId, alvo.clienteId, alvo.prazo);
            if (pend) {
              setVendoSemMov(null);
              setConfirmandoObs({ ...alvo, texto: pend.texto, acao: () => marcarSemMovimento(alvo.itemId, alvo.clienteId, alvo.prazo, false, { observacao_resolvida: true }) });
              return;
            }
            await marcarSemMovimento(alvo.itemId, alvo.clienteId, alvo.prazo, precisa); setVendoSemMov(null); carregar();
          }}
          onDesmarcar={async () => { await desmarcarSemMovimento(vendoSemMov.itemId, vendoSemMov.clienteId, vendoSemMov.prazo); setVendoSemMov(null); carregar(); }}
          onFechar={() => setVendoSemMov(null)} />
      )}
      {vendoParticularidade && (
        <ModalParticularidade alvo={vendoParticularidade}
          textoAtual={particularidadeNoMes(vendoParticularidade.itemId, vendoParticularidade.clienteId, vendoParticularidade.mes)?.texto || ''}
          desde={particularidadeNoMes(vendoParticularidade.itemId, vendoParticularidade.clienteId, vendoParticularidade.mes)?.vigencia}
          onSalvar={(t) => gravarParticularidade(vendoParticularidade, t)} onFechar={() => setVendoParticularidade(null)} />
      )}

      <div className={css.rodape}>
        <span>{k.total} rotinas no período <i>|</i> {linhasTabela.length} empresas{!verConcluidos && qtdLinhasConcluidas ? ` (+${qtdLinhasConcluidas} concluídas ocultas)` : ''}</span>
        <span className={css.paginacao}>
          Itens por página
          <select value={porPagina} onChange={e => setPorPagina(Number(e.target.value))}>
            {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {linhasTabela.length ? `${(paginaAtual - 1) * porPagina + 1}–${Math.min(paginaAtual * porPagina, linhasTabela.length)} de ${linhasTabela.length}` : '0 de 0'}
          <button type="button" disabled={paginaAtual <= 1} onClick={() => setPagina(paginaAtual - 1)}><FiChevronLeft size={15} /></button>
          <button type="button" disabled={paginaAtual >= totalPaginas} onClick={() => setPagina(paginaAtual + 1)}><FiChevronRight size={15} /></button>
        </span>
      </div>
    </div>
  );
}
